import {
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  DEFAULT_LABEL_COLUMN_WIDTH,
  DEFAULT_PADDING,
  DEFAULT_FONT,
  BUFFER_WIDTH,
} from "../constants"

// Reusable canvas instance for text measurements
let canvasContext = null
function getCanvasContext() {
  if (!canvasContext) {
    const canvas = document.createElement("canvas")
    canvasContext = canvas.getContext("2d")
  }
  return canvasContext
}

// Memoization cache for text width measurements
const textWidthCache = new Map()

/**
 * Measures the width of text using canvas
 * @param {string} text - The text to measure
 * @param {string} font - CSS font string (e.g., "12px Open Sans")
 * @returns {number} The width of the text in pixels
 */
export function getTextWidth(text, font = DEFAULT_FONT) {
  const cacheKey = `${text}:${font}`

  if (textWidthCache.has(cacheKey)) {
    return textWidthCache.get(cacheKey)
  }

  const ctx = getCanvasContext()
  ctx.font = font
  const width = ctx.measureText(text).width + BUFFER_WIDTH
  textWidthCache.set(cacheKey, width)
  return width
}

export function clearTextWidthCache() {
  textWidthCache.clear()
}

/**
 * Calculates the optimal width for a column based on text content
 * @param {Array} items - Array of items with text properties
 * @param {string} textProperty - Property name containing the text (default: 'label')
 * @param {string} font - CSS font string for measurement
 * @param {Object} options - Configuration options
 * @param {number} options.minWidth - Minimum column width (default: MIN_COLUMN_WIDTH)
 * @param {number} options.maxWidth - Maximum column width (default: MAX_COLUMN_WIDTH)
 * @param {number} options.defaultWidth - Default width if no text (default: DEFAULT_LABEL_COLUMN_WIDTH)
 * @param {number} options.padding - Additional padding to add (default: DEFAULT_PADDING)
 * @returns {number} The calculated optimal width
 */
export function calculateOptimalColumnWidth(
  items,
  textProperty = "label",
  font = DEFAULT_FONT,
  options = {},
) {
  const {
    minWidth = MIN_COLUMN_WIDTH,
    maxWidth = MAX_COLUMN_WIDTH,
    defaultWidth = DEFAULT_LABEL_COLUMN_WIDTH,
    padding = DEFAULT_PADDING,
  } = options

  if (!items || items.length === 0) return minWidth

  const textValues = items
    .map(item => item[textProperty] || "")
    .filter(Boolean)

  if (textValues.length === 0) return defaultWidth

  const longestText = textValues.reduce((longest, current) => {
    return current.length > longest.length ? current : longest
  }, "")

  const calculatedWidth = getTextWidth(longestText, font)
  const finalWidth = calculatedWidth + padding
  return Math.max(minWidth, Math.min(finalWidth, maxWidth))
}

/**
 * Calculates the optimal width for a list of text strings
 * @param {Array<string>} textArray - Array of text strings
 * @param {string} font - CSS font string for measurement
 * @param {Object} options - Configuration options
 * @param {number} options.minWidth - Minimum width (default: MIN_COLUMN_WIDTH)
 * @param {number} options.maxWidth - Maximum width (default: MAX_COLUMN_WIDTH)
 * @param {number} options.defaultWidth - Default width if no text (default: DEFAULT_LABEL_COLUMN_WIDTH)
 * @param {number} options.padding - Additional padding to add (default: DEFAULT_PADDING)
 * @returns {number} The calculated optimal width
 */
export function calculateOptimalTextWidth(
  textArray,
  font = DEFAULT_FONT,
  options = {},
) {
  const {
    minWidth = MIN_COLUMN_WIDTH,
    maxWidth = MAX_COLUMN_WIDTH,
    defaultWidth = DEFAULT_LABEL_COLUMN_WIDTH,
    padding = DEFAULT_PADDING,
  } = options

  if (!textArray || textArray.length === 0) return minWidth

  const validTexts = textArray.filter(Boolean)

  if (validTexts.length === 0) return defaultWidth

  const longestText = validTexts.reduce((longest, current) => {
    return current.length > longest.length ? current : longest
  }, "")

  const calculatedWidth = getTextWidth(longestText, font)
  const finalWidth = calculatedWidth + padding

  return Math.max(minWidth, Math.min(finalWidth, maxWidth))
}
